import { useState, useEffect, useRef } from 'react';
import { WifiOff } from 'lucide-react';
import { NETWORK_ERROR_BANNER_COOLDOWN_MS } from '@/utils/https';

/**
 * Indicateur de statut réseau (offline navigateur ou erreurs API transitoires).
 */
export default function NetworkStatusBanner() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [hasNetworkError, setHasNetworkError] = useState(false);
  const clearBannerTimeoutRef = useRef(null);

  useEffect(() => {
    const clearScheduledHide = () => {
      if (clearBannerTimeoutRef.current) {
        clearTimeout(clearBannerTimeoutRef.current);
        clearBannerTimeoutRef.current = null;
      }
    };

    const scheduleHide = () => {
      clearScheduledHide();
      clearBannerTimeoutRef.current = setTimeout(() => {
        setHasNetworkError(false);
        clearBannerTimeoutRef.current = null;
      }, NETWORK_ERROR_BANNER_COOLDOWN_MS);
    };

    const handleOnline = () => {
      setIsOnline(true);
      setHasNetworkError(false);
      clearScheduledHide();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setHasNetworkError(true);
      clearScheduledHide();
    };

    const handleNetworkError = () => {
      setHasNetworkError(true);
      scheduleHide();
    };

    const handleNetworkErrorClear = () => {
      setHasNetworkError(false);
      clearScheduledHide();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('network-error', handleNetworkError);
    window.addEventListener('network-error-clear', handleNetworkErrorClear);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('network-error', handleNetworkError);
      window.removeEventListener('network-error-clear', handleNetworkErrorClear);
      clearScheduledHide();
    };
  }, []);

  if (isOnline && !hasNetworkError) {
    return null;
  }

  return (
    <div className="bg-orange-500 w-full h-[40px] flex items-center justify-center text-white px-5 text-sm">
      <div className="flex items-center gap-2">
        {isOnline ? (
          <>
            <WifiOff className="h-4 w-4" />
            <span>Connection issues detected. Some features may not work properly.</span>
          </>
        ) : (
          <>
            <WifiOff className="h-4 w-4" />
            <span>You are currently offline. Please check your internet connection.</span>
          </>
        )}
      </div>
    </div>
  );
}
